import { CompanyWithRolesAndWorkCentersDTO, RoleDTO, WorkCenterDTO } from 'src/app/interfaces/base-entity';
import { UserRoleEnum } from '../enums/enums';

/** Objeto almacenado en `currentCompany` (puede ser CompanyDTO o CompanyWithRolesAndWorkCentersDTO). */
export type StoredCompanyContext = {
  id?: number;
  companyId?: number;
  workCenterId?: number;
  workCenters?: WorkCenterDTO[];
  roles?: RoleDTO[];
};

/** Prioridad de roles SST (mayor = preferido). */
export const TENANT_ROLE_PRIORITY: Record<number, number> = {
  [UserRoleEnum.Root]: 4,
  [UserRoleEnum.AdminCompany]: 3,
  [UserRoleEnum.AdminWorkCenter]: 2,
  [UserRoleEnum.Employee]: 1
};

export function selectBestTenantRole(roles: RoleDTO[] | null | undefined): RoleDTO | null {
  if (!roles?.length) {
    return null;
  }

  return roles.reduce((best, role) =>
    (TENANT_ROLE_PRIORITY[role.roleId] ?? 0) > (TENANT_ROLE_PRIORITY[best.roleId] ?? 0) ? role : best
  );
}

/**
 * Resuelve CompanyId desde storage (compatible con distintas formas del DTO).
 */
export function resolveCompanyId(currentCompany: StoredCompanyContext | null | undefined): number | null {
  if (!currentCompany) {
    return null;
  }
  const id = currentCompany.companyId ?? currentCompany.id;
  return id != null && id > 0 ? id : null;
}

/**
 * Resuelve WorkCenterId para headers API: rol actual → empresa → selección explícita.
 */
export function resolveWorkCenterId(
  currentCompany: StoredCompanyContext | null | undefined,
  currentRole: RoleDTO | null | undefined,
  selectedWorkCenterId?: number | null
): number | null {
  if (selectedWorkCenterId != null && selectedWorkCenterId > 0) {
    return selectedWorkCenterId;
  }

  if (currentCompany?.workCenterId != null && currentCompany.workCenterId > 0) {
    return currentCompany.workCenterId;
  }

  const roleCenters = currentRole?.workCenters ?? [];
  if (roleCenters.length === 1) {
    return roleCenters[0].id;
  }

  const companyCenters = currentCompany?.workCenters ?? [];
  if (companyCenters.length === 1) {
    return companyCenters[0].id;
  }

  if (roleCenters.length > 0) {
    return roleCenters[0].id;
  }

  if (companyCenters.length > 0) {
    return companyCenters[0].id;
  }

  return null;
}

export function readSelectedWorkCenterId(storage: {
  getItem(key: string, secure?: boolean): unknown;
}): number | null {
  const raw = storage.getItem('selectedWorkCenterId', false);
  if (raw == null || raw === '') {
    return null;
  }
  const id = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  return !isNaN(id) && id > 0 ? id : null;
}

export interface TenantHeadersContext {
  companyId: number | null;
  workCenterId: number | null;
  roleId: number | null;
}

function resolveCompanyFromList(storage: {
  getItem(key: string, secure?: boolean): unknown;
}): CompanyWithRolesAndWorkCentersDTO | null {
  const companies = storage.getItem('companyList', false) as CompanyWithRolesAndWorkCentersDTO[] | null;
  if (!companies?.length) {
    return null;
  }

  return companies.find(company => company.companyId != null && company.companyId > 0) ?? null;
}

export function resolveTenantHeaders(storage: {
  getItem(key: string, secure?: boolean): unknown;
}): TenantHeadersContext {
  const storedCompany = storage.getItem('currentCompany', false) as StoredCompanyContext | null;
  const fallbackCompany = resolveCompanyFromList(storage);
  const currentCompany = storedCompany ?? fallbackCompany;
  const storedRole = storage.getItem('currentRole', false) as RoleDTO | null;
  const currentRole = storedRole ?? selectBestTenantRole(fallbackCompany?.roles);
  const selectedWc = readSelectedWorkCenterId(storage);

  return {
    companyId: resolveCompanyId(currentCompany),
    workCenterId: resolveWorkCenterId(currentCompany, currentRole, selectedWc),
    roleId: currentRole?.roleId ?? null
  };
}
